import { Group, Vector3, Raycaster } from "three";
import { CharacterController } from "@controllers/character";
import { MapConfig } from "@objects/terrain";
import { MathUtils } from "@utils/math";
import { BoneController } from "./bones";
class Entity {
    position;
    raycaster;
    rotation;
    dimensions;
    group;
    maxHealth;
    finiteStateMachine;
    meshController;
    skeleton;
    gunController;
    health;
    characterController;
    /**
     * Entity object.
     * @param config the entity's configuration object.
     * @param scene the rendering scene.
     * @param physicsWorld the physics world.
     * @param meshController Object responsible for handling the entity's mesh.
     * @param inputController the entity's input controller.
     * @param finiteStateMachine controls the entity's animation states.
     * @param gunController The gun controller of an entity.
     */
    constructor(config, scene, physicsWorld, meshController, inputController, finiteStateMachine, gunController) {
        this.raycaster = new Raycaster();
        this.meshController = meshController;
        this.position = config.POSITION;
        this.rotation = config.ROTATION;
        this.group = this.createGroup();
        this.group.add(this.meshController.getMesh);
        this.dimensions = this.meshController.getDimensions();
        this.finiteStateMachine = finiteStateMachine;
        this.characterController = new CharacterController(config, this.position, this.rotation, this.meshController, physicsWorld, inputController);
        this.maxHealth = config.MAX_HEALTH;
        this.health = this.maxHealth;
        this.skeleton = {
            eyes: new BoneController("Eyes", this.meshController.getMesh),
            chest: new BoneController("Chest", this.meshController.getMesh),
            handLeft: new BoneController("HandL", this.meshController.getMesh),
            handRight: new BoneController("HandR", this.meshController.getMesh),
        };
        this.gunController = gunController;
        this.skeleton.chest.getBone.add(this.gunController.getGroup);
        scene.add(this.group);
    }
    /**
     * Perform an update.
     * @param entities The entities that the entity can aim at.
     * @param ground the game's ground.
     * @param deltaTime The interval in seconds from the last frame to the current one.
     */
    step(entities, ground, deltaTime) {
        if (this.isDead)
            return;
        this.characterController.step();
        this.synchronizeTransform();
        this.verifyBounds();
        this.finiteStateMachine.update(deltaTime, this.characterController.getInputController);
        this.skeleton.chest.getBone.quaternion.copy(MathUtils.getRotationAlongAxis(this.rotation, new Vector3(1, 0, 0), "x", "YZX"));
        this.updateRaycaster();
        this.gunController.step(this, entities, ground, this.characterController.getInputController, deltaTime);
    }
    /**
     * Synchronize the entity's position and rotation with the controller ghost object.
     * @private
     */
    synchronizeTransform() {
        this.position.copy(this.characterController.getPosition);
        this.group.position.copy(this.position);
        this.rotation.copy(this.characterController.getRotation);
        this.group.quaternion.copy(MathUtils.getRotationAlongAxis(this.rotation, new Vector3(0, 1, 0), "y", "YZX"));
    }
    /**
     * Update the raycaster so that it is looking towards where the entity's eyes are looking.
     */
    updateRaycaster() {
        const origin = new Vector3();
        const direction = new Vector3();
        this.skeleton.eyes.getBone.getWorldPosition(origin);
        this.skeleton.eyes.getBone.getWorldDirection(direction);
        direction.normalize();
        direction.negate();
        this.raycaster.set(origin, direction);
    }
    /**
     * Kill the entity if he is in the void or above the sky limit.
     * @private
     */
    verifyBounds() {
        if (this.position.y <= MapConfig.VOID_LEVEL || this.position.y >= MapConfig.SKY_LEVEL)
            this.updateHealth(-this.maxHealth);
    }
    /**
     * Create a group object.
     * @returns the create group object.
     * @private
     */
    createGroup() {
        const group = new Group();
        group.position.copy(this.position);
        group.quaternion.copy(this.rotation);
        return group;
    }
    /**
     * Update the entity's health.
     * If the `amount` parameter is negative, health will be reduced.
     * Updates the health store if the entity is a player.
     * @param amount the amount of health to add or remove.
     */
    updateHealth(amount) {
        this.health = MathUtils.clamp(0, this.health + amount, this.maxHealth);
    }
    /**
     * Kill an entity.
     * @param scene The scene to remove the entity from.
     * @param physicsWorld The physics world to remove to entity from.
     */
    kill(scene, physicsWorld) {
        this.health = 0;
        this.remove(scene, physicsWorld);
    }
    /**
     * Remove an entity from the physics world and rendering scene.
     * @param scene The scene to remove the entity from.
     * @param physicsWorld The physics world to remove to entity from.
     */
    remove(scene, physicsWorld) {
        scene.remove(this.getGroup);
        physicsWorld.removeCollisionObject(this.characterController.getCharacterController.getGhostObject());
        physicsWorld.removeAction(this.characterController.getCharacterController);
    }
    get getGroup() {
        return this.group;
    }
    get getRaycaster() {
        return this.raycaster;
    }
    get getSkeleton() {
        return this.skeleton;
    }
    get getGun() {
        return this.gunController.getSelected;
    }
    get isDead() {
        return this.health <= 0;
    }
    get getPosition() {
        return this.position;
    }
    get getRotation() {
        return this.rotation;
    }
    get getHealth() {
        return this.health;
    }
}
export default Entity;
